import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { fetchStats } from "@/lib/api";

function formatNum(n) {
  if (n >= 1e9) {
    const v = n / 1e9;
    return `${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)}B`;
  }
  if (n >= 1e6) {
    const v = n / 1e6;
    return `${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)}M`;
  }
  if (n >= 1e3) {
    const v = n / 1e3;
    return `${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)}K`;
  }
  return String(n);
}

function Counter({ value, suffix = "", format = formatNum }) {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || startedRef.current) return;
    const runAnim = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      const start = performance.now();
      const duration = 1600;
      const tick = (t) => {
        const p = Math.min(1, (t - start) / duration);
        const eased = 1 - Math.pow(1 - p, 3);
        setN(Math.floor(value * eased));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };
    if (typeof IntersectionObserver === "undefined") { runAnim(); return; }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) { runAnim(); io.disconnect(); break; }
        }
      },
      { threshold: 0.01, rootMargin: "0px 0px -10% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [value]);

  return <span ref={ref}>{format(n)}{suffix}</span>;
}

export default function Stats() {
  const [stats, setStats] = useState(null);
  useEffect(() => { fetchStats().then(setStats).catch(() => {}); }, []);

  if (!stats) return null;

  const items = [
    { label: "Campaign views generated", value: stats.campaign_views, key: "views", suffix: "+" },
    { label: "Active creators with direct rates and contacts", value: stats.creator_partners, key: "creators", suffix: "+" },
    { label: "Artists worked with", value: stats.artists, key: "artists", suffix: "+" },
    { label: "Record labels", value: stats.labels, key: "labels", suffix: "+" },
    { label: "Campaigns and promos completed", value: stats.campaigns_completed, key: "campaigns", suffix: "+" },
    { label: "Turnaround time", value: stats.avg_turnaround_days, key: "turnaround", textOverride: "24–48 hrs" },
  ];

  return (
    <section data-testid="home-stats" className="relative py-10 md:py-14 bg-black">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.24em] text-zinc-500 mb-4">The Prulo standard</div>
            <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight max-w-2xl">
              The numbers labels<br />chase. We set them.
            </h2>
          </div>
          <p className="max-w-sm text-zinc-400">
            Every metric is a song that broke, a fan that got reached,
            a chart position that came home. <span className="italic text-zinc-500">Not marketing speak.</span>
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 border-t border-white/10">
          {items.map((it, i) => (
            <motion.div
              key={it.key}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.7, delay: i * 0.06 }}
              className={`p-8 md:p-10 border-b border-white/10 ${
                i % 3 !== 2 ? "md:border-r border-white/10" : ""
              } ${i % 2 !== 1 ? "border-r md:border-r border-white/10" : ""}`}
              data-testid={`stat-${it.key}`}
            >
              <div className="font-display font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight">
                {it.textOverride ? it.textOverride : <Counter value={it.value} suffix={it.suffix || ""} format={it.format || formatNum} />}
              </div>
              <div className="mt-3 text-sm text-zinc-500">{it.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
